import { Component, inject, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';
import { AuthApiService } from '../../apiCalls/auth-api.service';
import { CustomSnackbarService } from '../../shared/custom-snackbar.service';
import { passwordsMatchValidator, strongPasswordValidator } from '../../shared/password-validator';
import { RoutesConst } from '../../shared/route-const';

interface ChangePasswordForm {
  currentPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
}

export interface ChangePasswordFormValue {
  oldPassword: string;
  newPassword: string;
}

@Component({
  selector: 'app-change-password',
  imports: [MatButtonModule, MatDialogModule, MatFormField, MatInputModule, ReactiveFormsModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent implements OnDestroy {
  private readonly subs = new SubSink();

  // Injected dependencies
  private readonly authApiSvc = inject(AuthApiService);

  private readonly fb = inject(FormBuilder);
  private readonly customSnackbarSvc = inject(CustomSnackbarService);
  private readonly router = inject(Router);

  /** Reactive signals for state management */
  apiErrorMessage = signal<string>('');
  saveState = signal<boolean>(false);

  /** Form definition */
  form: FormGroup<ChangePasswordForm> = this.fb.nonNullable.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, strongPasswordValidator()]],
      confirmPassword: ['', [Validators.required]],
    },
    {
      validators: passwordsMatchValidator('newPassword', 'confirmPassword'),
    },
  );

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value: ChangePasswordFormValue = {
      oldPassword: this.form.value.currentPassword || '',
      newPassword: this.form.value.newPassword || '',
    };

    this.saveState.set(true);
    this.clearErrorMessage();

    this.customSnackbarSvc.requestSnackbar();

    this.subs.sink = this.authApiSvc.changePassword(value).subscribe({
      next: () => this.handleSuccess(),
      error: (error) => this.handleError(error.message),
    });
  }

  /**
   * Handles successful
   */
  private handleSuccess(): void {
    this.clearErrorMessage();
    this.saveState.set(false);
    this.routeBack();
    this.customSnackbarSvc.successSnackbar(' Password changed successfully');
  }

  /**
   * Handles errors
   */
  private handleError(message: string): void {
    const errorMessage = message || 'Unknown error occurred';
    this.apiErrorMessage.set(`: ${errorMessage}`);
    console.error('Error:', errorMessage);
    this.customSnackbarSvc.errorSnackbar();
    this.saveState.set(false);
  }

  /**
   * Clears the API error message
   */
  private clearErrorMessage(): void {
    this.apiErrorMessage.set('');
  }

  routeBack() {
    this.router.navigate([RoutesConst.myProfile]);
  }

  // Getters for easier access in the template
  get currentPassword() {
    return this.form.controls.currentPassword;
  }
  get newPassword() {
    return this.form.controls.newPassword;
  }
  get confirmPassword() {
    return this.form.controls.confirmPassword;
  }
}
